import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Cpu, Plus, Lock, Unlock, Camera, Radio, Thermometer, Lightbulb, Zap, Trash2, DoorOpen } from "lucide-react";

const CAT_ICON = { lock: Lock, camera: Camera, sensor: Radio, thermostat: Thermometer, lighting: Lightbulb, access_controller: DoorOpen, energy_meter: Zap, gateway: Radio };
const STATUS_TONE = { online: "bg-emerald-500/12 text-emerald-600 border-emerald-500/30", offline: "bg-muted text-muted-foreground", fault: "bg-destructive/12 text-destructive border-destructive/30", unknown: "" };

export default function SmartDevices() {
    const [meta, setMeta] = useState({ connectors: [], categories: [], protocols: [] });
    const [devices, setDevices] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [events, setEvents] = useState([]);
    const [filter, setFilter] = useState("all");
    const [addOpen, setAddOpen] = useState(false);
    const [form, setForm] = useState({ category: "lock", connector: "", room_id: "none", name: "", model: "" });

    const load = async () => {
        try {
            const [c, d, r, e] = await Promise.all([
                api.get("/devices/connectors"), api.get("/devices"), api.get("/rooms"), api.get("/devices/events?limit=25"),
            ]);
            setMeta(c.data); setDevices(d.data); setRooms(r.data); setEvents(e.data);
        } catch (err) { toast.error(err.response?.data?.detail || "Failed to load devices"); }
    };
    useEffect(() => { load(); }, []);

    const connectorsForCat = useMemo(() => meta.connectors.filter((c) => c.categories.includes(form.category)), [meta, form.category]);

    const openAdd = () => { setForm({ category: "lock", connector: "", room_id: "none", name: "", model: "" }); setAddOpen(true); };

    const create = async () => {
        if (!form.connector) return toast.error("Pick a connector");
        try {
            await api.post("/devices", {
                name: form.name || undefined, category: form.category, connector: form.connector,
                model: form.model || undefined, room_id: form.room_id === "none" ? null : form.room_id,
            });
            toast.success("Device added"); setAddOpen(false); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
    };

    const command = async (id, action) => {
        try { await api.post(`/devices/${id}/command`, { action }); toast.success(action); load(); }
        catch { toast.error("Command failed"); }
    };
    const simulate = async (id) => {
        try { await api.post(`/devices/${id}/simulate`, {}); toast.success("Simulated"); load(); }
        catch { toast.error("Simulate failed"); }
    };
    const remove = async (id) => {
        try { await api.delete(`/devices/${id}`); toast.success("Removed"); load(); }
        catch { toast.error("Delete failed"); }
    };
    const seed = async () => {
        try { const { data } = await api.post("/devices/seed-demo", {}); toast.success(`Seeded ${data.created} devices`); load(); }
        catch { toast.error("Seed failed"); }
    };

    const shown = filter === "all" ? devices : devices.filter((d) => d.category === filter);

    return (
        <div className="space-y-5" data-testid="smart-devices-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Cpu className="w-3.5 h-3.5" /> Operations</div>
                    <h1 className="text-2xl font-semibold tracking-tight mt-1">Smart Devices</h1>
                    <p className="text-muted-foreground mt-1 text-sm max-w-2xl">Locks, cameras, sensors and room automation across popular hotel hardware. Every connector runs in mock mode until you connect real credentials.</p>
                </div>
                <div className="flex gap-2">
                    {devices.length === 0 && <Button variant="outline" onClick={seed} data-testid="devices-seed"><Zap className="w-4 h-4 mr-1" />Seed demo fleet</Button>}
                    <Button onClick={openAdd} data-testid="devices-add"><Plus className="w-4 h-4 mr-1" />Add device</Button>
                </div>
            </div>

            {/* Connector catalog */}
            <div>
                <div className="text-sm font-medium mb-2">Supported connectors ({meta.connectors.length})</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="devices-connector-catalog">
                    {meta.connectors.map((c) => (
                        <Card key={c.id}><CardContent className="py-3 space-y-1">
                            <div className="flex items-center justify-between gap-2">
                                <span className="font-medium text-sm">{c.name}</span>
                                <div className="flex gap-1">{c.categories.slice(0, 3).map((cat) => { const I = CAT_ICON[cat] || Radio; return <I key={cat} className="w-3.5 h-3.5 text-muted-foreground" />; })}</div>
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-relaxed">{c.note}</p>
                            <div className="flex flex-wrap gap-1 pt-0.5">{c.protocols.map((p) => <Badge key={p} variant="outline" className="text-[9px] px-1 py-0">{p}</Badge>)}</div>
                        </CardContent></Card>
                    ))}
                </div>
            </div>

            {/* Devices table */}
            <Card>
                <CardHeader className="pb-2 flex-row items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="text-base">Fleet ({devices.length})</CardTitle>
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="h-8 w-40" data-testid="devices-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All categories</SelectItem>
                            {meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </CardHeader>
                <CardContent>
                    {shown.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">No devices — add one or seed the demo fleet.</div> : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="devices-table">
                                <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
                                    <th className="py-2 pr-3">Device</th><th className="py-2 pr-3">Room</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">State</th><th className="py-2 pr-3 text-right">Actions</th>
                                </tr></thead>
                                <tbody>
                                    {shown.map((d) => { const I = CAT_ICON[d.category] || Radio; return (
                                        <tr key={d.id} className="border-b border-border/60">
                                            <td className="py-2 pr-3"><div className="flex items-center gap-2"><I className="w-4 h-4 text-muted-foreground" /><div><div className="font-medium">{d.name}</div><div className="text-[10px] text-muted-foreground">{d.vendor} · {d.protocol}</div></div></div></td>
                                            <td className="py-2 pr-3 text-xs">{d.room_number || (d.zone || "—")}</td>
                                            <td className="py-2 pr-3"><Badge variant="outline" className={STATUS_TONE[d.status] || ""}>{d.status}</Badge></td>
                                            <td className="py-2 pr-3 text-[11px] text-muted-foreground">
                                                {d.category === "lock" && (d.state?.locked === false ? "unlocked" : "locked")}
                                                {d.state?.battery != null && ` · ${d.state.battery}%`}
                                                {d.state?.temp != null && ` · ${d.state.temp}°`}
                                            </td>
                                            <td className="py-2 pr-3">
                                                <div className="flex items-center gap-1 justify-end">
                                                    {d.category === "lock" && <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => command(d.id, d.state?.locked === false ? "lock" : "unlock")} data-testid={`device-lock-${d.id}`}>{d.state?.locked === false ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}</Button>}
                                                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => simulate(d.id)} title="Simulate event" data-testid={`device-sim-${d.id}`}><Zap className="w-4 h-4" /></Button>
                                                    <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive" onClick={() => remove(d.id)}><Trash2 className="w-4 h-4" /></Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ); })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Event feed */}
            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Recent events</CardTitle></CardHeader>
                <CardContent>
                    <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="devices-events">
                        {events.map((e) => (
                            <div key={e.id} className="flex items-center gap-2 text-sm">
                                <span className={`w-1.5 h-1.5 rounded-full ${e.severity === "critical" ? "bg-destructive" : e.severity === "warning" ? "bg-amber-500" : "bg-muted-foreground/50"}`} />
                                <span className="font-medium">{e.type}</span>
                                <Badge variant="outline" className="text-[9px] px-1 py-0">{e.source}</Badge>
                                <span className="text-xs text-muted-foreground ml-auto">{(e.ts || "").slice(0, 16).replace("T", " ")}</span>
                            </div>
                        ))}
                        {events.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">No events yet.</div>}
                    </div>
                </CardContent>
            </Card>

            {/* Add dialog */}
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Add device</DialogTitle><DialogDescription>Pick a category, then a connector from that category's supported hardware.</DialogDescription></DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Category</Label>
                                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, connector: "" })}>
                                    <SelectTrigger data-testid="device-form-category"><SelectValue /></SelectTrigger>
                                    <SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Room (optional)</Label>
                                <Select value={form.room_id} onValueChange={(v) => setForm({ ...form, room_id: v })}>
                                    <SelectTrigger data-testid="device-form-room"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">— none / common area —</SelectItem>
                                        {rooms.map((r) => <SelectItem key={r.id} value={r.id}>Room {r.number} (F{r.floor})</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label>Connector</Label>
                            <Select value={form.connector} onValueChange={(v) => setForm({ ...form, connector: v })}>
                                <SelectTrigger data-testid="device-form-connector"><SelectValue placeholder="Select hardware" /></SelectTrigger>
                                <SelectContent>{connectorsForCat.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Name (optional)</Label>
                            <Input value={form.name} placeholder="e.g. Lock 204" onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="device-form-name" />
                        </div>
                    </div>
                    <DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button><Button onClick={create} data-testid="device-form-save">Add device</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
